/**
 * Teks resmi UU No. 9 Tahun 2018 dan PP No. 44 Tahun 2025 tentang PNBP.
 * Sumber UU: Lembaran Negara RI Tahun 2018 Nomor 147, TLN Nomor 6245. Disahkan: 23 Agustus 2018.
 * Sumber PP: Ditetapkan Tahun 2025, mengatur tata cara penetapan tarif, pengelolaan, dan penyelesaian keberatan/keringanan/pengembalian PNBP.
 */

export interface PasalAyat {
  label: string;
  teks: string;
  sub?: { label: string; teks: string }[];
}

export interface PasalData {
  key: string;
  referensi: string;
  bab: string;
  judul: string;
  pembuka?: string;
  ayat: PasalAyat[];
  catatan?: string;
}

// ─── Normalisasi referensi ke key ────────────────────────────────────────────

function pasalRefToKeys(ref: string): string[] {
  // PP 44/2025 range, e.g. "Pasal 5-8 PP 44/2025"
  const pp44Range = ref.match(/^Pasal\s+(\d+)[-–](\d+)\s+PP\s+44\/2025$/i);
  if (pp44Range) {
    const start = parseInt(pp44Range[1]);
    const end = parseInt(pp44Range[2]);
    const keys: string[] = [];
    for (let i = start; i <= end && keys.length < 6; i++) keys.push(`pp44-pasal-${i}`);
    return keys;
  }

  // PP 44/2025 single, e.g. "Pasal 19 PP 44/2025"
  const pp44Single = ref.match(/^Pasal\s+(\d+)\s+PP\s+44\/2025$/i);
  if (pp44Single) return [`pp44-pasal-${pp44Single[1]}`];

  // UU 9/2018 and bare references
  const clean = ref
    .replace(/\s+UU\s+\d+\/\d+/gi, "")
    .replace(/\s+PP\s+\d+\/\d+/gi, "")
    .trim();

  if (/^Penjelasan\s+Umum$/i.test(clean)) return ["penjelasan-umum"];
  if (/^Penjelasan\s+Fungsi/i.test(clean)) return ["penjelasan-fungsi-pnbp"];

  const range = clean.match(/^Pasal\s+(\d+)[-–](\d+)$/i);
  if (range) {
    const start = parseInt(range[1]);
    const end = parseInt(range[2]);
    const keys: string[] = [];
    for (let i = start; i <= end && keys.length < 6; i++) keys.push(`pasal-${i}`);
    return keys;
  }

  const single = clean.match(/^Pasal\s+(\d+)$/i);
  if (single) return [`pasal-${single[1]}`];

  return [];
}

export function lookupPasalRefs(refs: string[]): PasalData[] {
  const seen = new Set<string>();
  const result: PasalData[] = [];
  for (const ref of refs) {
    for (const key of pasalRefToKeys(ref)) {
      if (!seen.has(key) && PASAL_DB[key]) {
        seen.add(key);
        result.push(PASAL_DB[key]);
      }
    }
  }
  return result;
}

// ─── Database teks pasal ──────────────────────────────────────────────────────

export const PASAL_DB: Record<string, PasalData> = {

  "penjelasan-umum": {
    key: "penjelasan-umum",
    referensi: "Penjelasan Umum UU No. 9 Tahun 2018",
    bab: "Penjelasan Umum",
    judul: "Latar Belakang Pembentukan UU PNBP",
    pembuka:
      "UU No. 9 Tahun 2018 dibentuk karena pengelolaan PNBP masih menghadapi berbagai permasalahan dan tantangan yang perlu diselesaikan.",
    ayat: [
      {
        label: "Permasalahan",
        teks:
          "Pengelolaan PNBP masih menghadapi berbagai permasalahan dan tantangan, antara lain adanya pungutan tanpa dasar hukum, terlambat/tidak disetor ke Kas Negara, penggunaan langsung PNBP, dan PNBP dikelola di luar mekanisme anggaran pendapatan dan belanja negara.",
      },
      {
        label: "Asas",
        teks:
          "Penyempurnaan pengaturan pengelolaan PNBP dalam Undang-Undang ini berlandaskan asas keadilan, asas kepastian hukum, asas daya pikul, asas manfaat, asas keterbukaan, dan asas akuntabilitas.",
      },
    ],
    catatan:
      "UU ini menggantikan UU No. 20 Tahun 1997 tentang Penerimaan Negara Bukan Pajak yang sudah tidak sesuai dengan perkembangan hukum, tata kelola, pengelolaan keuangan negara, dan kebutuhan masyarakat.",
  },

  "penjelasan-fungsi-pnbp": {
    key: "penjelasan-fungsi-pnbp",
    referensi: "Penjelasan Umum UU No. 9 Tahun 2018",
    bab: "Penjelasan Umum — Fungsi PNBP",
    judul: "Dua Fungsi Utama PNBP",
    pembuka:
      "PNBP pada prinsipnya memiliki dua fungsi, yaitu fungsi penganggaran (budgetary) dan fungsi pengaturan (regulatory).",
    ayat: [
      {
        label: "Budgetary",
        teks:
          "Selaku fungsi penganggaran (budgetary), PNBP merupakan salah satu pilar pendapatan negara yang memiliki kontribusi cukup besar dalam menunjang anggaran pendapatan dan belanja negara, melalui optimalisasi penerimaan negara.",
      },
      {
        label: "Regulatory",
        teks:
          "Selaku fungsi pengaturan (regulatory), PNBP memegang peranan penting dan strategis dalam mendukung kebijakan Pemerintah untuk pengendalian dan pengelolaan kekayaan negara termasuk pemanfaatan sumber daya alam. Pengendalian dan pengelolaan tersebut sangat penting artinya untuk mewujudkan kesejahteraan masyarakat, kemandirian bangsa, dan pembangunan nasional yang berkelanjutan dan berkeadilan.",
      },
    ],
  },

  "pasal-1": {
    key: "pasal-1",
    referensi: "Pasal 1 UU No. 9 Tahun 2018",
    bab: "BAB I — Ketentuan Umum",
    judul: "Pengertian",
    pembuka: "Dalam Undang-Undang ini yang dimaksud dengan:",
    ayat: [
      {
        label: "1.",
        teks:
          "Penerimaan Negara Bukan Pajak yang selanjutnya disingkat PNBP adalah pungutan yang dibayar oleh orang pribadi atau badan dengan memperoleh manfaat langsung maupun tidak langsung atas layanan atau pemanfaatan sumber daya dan hak yang diperoleh negara, berdasarkan peraturan perundang-undangan, yang menjadi penerimaan Pemerintah Pusat di luar penerimaan perpajakan dan hibah dan dikelola dalam mekanisme anggaran pendapatan dan belanja negara.",
      },
      {
        label: "4.",
        teks:
          "Wajib Bayar adalah orang pribadi atau Badan dari dalam negeri atau luar negeri yang mempunyai kewajiban membayar PNBP sesuai dengan ketentuan peraturan perundang-undangan.",
      },
      {
        label: "5.",
        teks:
          "Pemanfaatan Sumber Daya Alam adalah pemanfaatan bumi, air, udara, ruang angkasa, dan kekayaan alam yang terkandung di dalamnya yang dikuasai oleh negara.",
      },
      {
        label: "6.",
        teks:
          "Pelayanan adalah segala bentuk penyediaan barang, jasa, atau pelayanan administratif yang menjadi tanggung jawab Pemerintah, baik dalam pemenuhan kebutuhan masyarakat maupun pelaksanaan ketentuan peraturan perundang-undangan.",
      },
      {
        label: "7.",
        teks:
          "Pengelolaan Kekayaan Negara Dipisahkan adalah pengelolaan atas kekayaan negara yang berasal dari anggaran pendapatan dan belanja negara yang dijadikan penyertaan modal negara atau perolehan lain yang sah.",
      },
      {
        label: "8.",
        teks:
          "Pengelolaan Barang Milik Negara adalah kegiatan penggunaan, pemanfaatan, dan pemindahtanganan semua barang yang dibeli atau diperoleh atas beban anggaran pendapatan dan belanja negara atau berasal dari perolehan lain yang sah.",
      },
      {
        label: "9.",
        teks:
          "Pengelolaan Dana adalah pengelolaan atas dana pemerintah yang berasal dari anggaran pendapatan dan belanja negara atau perolehan lain yang sah untuk tujuan tertentu.",
      },
      {
        label: "10.",
        teks:
          "Hak Negara Lainnya adalah hak negara selain dari Pemanfaatan Sumber Daya Alam, Pelayanan, Pengelolaan Kekayaan Negara Dipisahkan, Pengelolaan Barang Milik Negara, Pengelolaan Dana, dan yang diatur sesuai dengan ketentuan peraturan perundang-undangan.",
      },
      {
        label: "16.",
        teks:
          "Instansi Pengelola PNBP adalah instansi yang menyelenggarakan pengelolaan PNBP.",
      },
      {
        label: "17.",
        teks:
          "Mitra Instansi Pengelola PNBP adalah Badan yang membantu Instansi Pengelola PNBP melaksanakan sebagian kegiatan pengelolaan PNBP yang menjadi tugas Instansi Pengelola PNBP berdasarkan ketentuan peraturan perundang-undangan.",
      },
      {
        label: "18.",
        teks:
          "Pengelolaan PNBP adalah pemanfaatan sumber daya dalam rangka tata kelola yang meliputi kegiatan perencanaan, pelaksanaan, pertanggungjawaban, dan pengawasan untuk meningkatkan pelayanan, akuntabilitas, dan optimalisasi penerimaan negara yang berasal dari PNBP.",
      },
      {
        label: "19.",
        teks:
          "PNBP Terutang adalah kewajiban PNBP dari Wajib Bayar kepada Pemerintah yang wajib dibayar pada waktu tertentu sesuai dengan ketentuan peraturan perundang-undangan.",
      },
    ],
  },

  "pasal-2": {
    key: "pasal-2",
    referensi: "Pasal 2 UU No. 9 Tahun 2018",
    bab: "BAB I — Ketentuan Umum",
    judul: "Tujuan Pengaturan PNBP",
    pembuka: "Pengaturan PNBP bertujuan untuk:",
    ayat: [
      {
        label: "a.",
        teks:
          "mewujudkan peningkatan kemandirian bangsa dengan mengoptimalkan sumber pendapatan negara dari PNBP guna memperkuat ketahanan fiskal, dan mendukung pembangunan nasional yang berkelanjutan dan berkeadilan;",
      },
      {
        label: "b.",
        teks:
          "mendukung kebijakan Pemerintah dalam rangka perbaikan kesejahteraan rakyat, peningkatan pertumbuhan ekonomi yang berkualitas, perbaikan distribusi pendapatan, dan pelestarian lingkungan hidup untuk kesinambungan antargenerasi dengan tetap mempertimbangkan aspek keadilan; dan",
      },
      {
        label: "c.",
        teks:
          "mewujudkan pelayanan Pemerintah yang bersih, profesional, transparan, dan akuntabel, untuk mendukung tata kelola pemerintahan yang baik serta meningkatkan pelayanan kepada masyarakat.",
      },
    ],
  },

  "pasal-3": {
    key: "pasal-3",
    referensi: "Pasal 3 UU No. 9 Tahun 2018",
    bab: "BAB II — Objek dan Subjek PNBP",
    judul: "Definisi dan Kriteria Objek PNBP",
    ayat: [
      {
        label: "(1)",
        teks:
          "Seluruh aktivitas, hal, dan/atau benda, yang menjadi sumber penerimaan negara di luar perpajakan dan hibah dinyatakan sebagai objek PNBP.",
      },
      {
        label: "(2)",
        teks: "Objek PNBP sebagaimana dimaksud pada ayat (1) memiliki kriteria:",
        sub: [
          { label: "a.", teks: "pelaksanaan tugas dan fungsi Pemerintah;" },
          {
            label: "b.",
            teks: "penggunaan dana yang bersumber dari anggaran pendapatan dan belanja negara;",
          },
          { label: "c.", teks: "pengelolaan kekayaan negara; dan/atau" },
          { label: "d.", teks: "penetapan peraturan perundang-undangan." },
        ],
      },
    ],
    catatan:
      'Yang dimaksud dengan "penetapan peraturan perundang-undangan" adalah seluruh kegiatan, peristiwa, dan kondisi yang berdasarkan peraturan perundangan-undangan dapat menimbulkan PNBP.',
  },

  "pasal-4": {
    key: "pasal-4",
    referensi: "Pasal 4 UU No. 9 Tahun 2018",
    bab: "BAB II — Objek dan Subjek PNBP",
    judul: "Jenis Objek PNBP",
    ayat: [
      {
        label: "(1)",
        teks: "Objek PNBP sebagaimana dimaksud dalam Pasal 3 meliputi:",
        sub: [
          { label: "a.", teks: "Pemanfaatan Sumber Daya Alam;" },
          { label: "b.", teks: "Pelayanan;" },
          { label: "c.", teks: "Pengelolaan Kekayaan Negara Dipisahkan;" },
          { label: "d.", teks: "Pengelolaan Barang Milik Negara;" },
          { label: "e.", teks: "Pengelolaan Dana; dan" },
          { label: "f.", teks: "Hak Negara Lainnya." },
        ],
      },
      {
        label: "(2)",
        teks: "Objek PNBP sebagaimana dimaksud pada ayat (1) dirinci menurut jenis.",
      },
      {
        label: "(3)",
        teks:
          "Jenis PNBP sebagaimana dimaksud pada ayat (2) diatur dengan Undang-Undang, Peraturan Pemerintah, dan/atau Peraturan Menteri.",
      },
    ],
  },

  "pasal-5": {
    key: "pasal-5",
    referensi: "Pasal 5 UU No. 9 Tahun 2018",
    bab: "BAB II — Objek dan Subjek PNBP",
    judul: "Subjek PNBP dan Wajib Bayar",
    ayat: [
      {
        label: "(1)",
        teks:
          "Subjek PNBP meliputi: a. orang pribadi; dan b. Badan, dari dalam negeri atau luar negeri yang menggunakan, memperoleh manfaat, dan/atau memiliki kaitan dengan objek PNBP sebagaimana dimaksud dalam Pasal 4.",
      },
      {
        label: "(2)",
        teks:
          "Subjek PNBP sebagaimana dimaksud pada ayat (1) merupakan Wajib Bayar dalam hal memiliki kewajiban membayar PNBP sesuai dengan ketentuan peraturan perundang-undangan.",
      },
    ],
  },

  "pasal-6": {
    key: "pasal-6",
    referensi: "Pasal 6 UU No. 9 Tahun 2018",
    bab: "BAB III — Tarif atas Jenis PNBP",
    judul: "Bentuk Tarif PNBP",
    pembuka: "Tarif atas jenis PNBP berbentuk:",
    ayat: [
      {
        label: "a.",
        teks: "tarif spesifik; dan/atau",
      },
      {
        label: "b.",
        teks: "tarif ad valorem.",
      },
    ],
    catatan:
      'Tarif spesifik adalah tarif yang ditetapkan dengan nilai nominal uang (contoh: Rp5.000.000,00/satuan). Tarif ad valorem adalah tarif yang ditetapkan dengan persentase dan formula (contoh: 10% × dasar perhitungan tertentu; dasar perhitungan antara lain harga patokan, harga jual, indeks harga, atau keuntungan bersih).',
  },

  "pasal-7": {
    key: "pasal-7",
    referensi: "Pasal 7 UU No. 9 Tahun 2018",
    bab: "BAB III — Tarif atas Jenis PNBP",
    judul: "Tarif Pemanfaatan Sumber Daya Alam",
    ayat: [
      {
        label: "(1)",
        teks:
          "Tarif atas jenis PNBP yang berasal dari Pemanfaatan Sumber Daya Alam sebagaimana dimaksud dalam Pasal 4 ayat (1) huruf a terdiri atas: a. tarif Pemanfaatan Sumber Daya Alam yang terbarukan; dan b. tarif Pemanfaatan Sumber Daya Alam yang tak terbarukan.",
      },
      {
        label: "(2)",
        teks:
          "Tarif atas jenis PNBP yang berasal dari Pemanfaatan Sumber Daya Alam sebagaimana dimaksud pada ayat (1) disusun dengan mempertimbangkan:",
        sub: [
          { label: "a.", teks: "nilai manfaat, kadar, atau kualitas sumber daya alam;" },
          {
            label: "b.",
            teks: "dampak pengenaan tarif terhadap masyarakat, dunia usaha, pelestarian alam dan lingkungan, serta sosial budaya;",
          },
          { label: "c.", teks: "aspek keadilan; dan/atau" },
          { label: "d.", teks: "kebijakan Pemerintah." },
        ],
      },
      {
        label: "(3)",
        teks:
          "Tarif atas jenis PNBP yang berasal dari Pemanfaatan Sumber Daya Alam sebagaimana dimaksud pada ayat (1) diatur dengan Undang-Undang, kontrak, dan/atau Peraturan Pemerintah.",
      },
    ],
  },

  "pasal-8": {
    key: "pasal-8",
    referensi: "Pasal 8 UU No. 9 Tahun 2018",
    bab: "BAB III — Tarif atas Jenis PNBP",
    judul: "Tarif Pelayanan",
    ayat: [
      {
        label: "(1)",
        teks:
          "Tarif atas jenis PNBP yang berasal dari Pelayanan sebagaimana dimaksud dalam Pasal 4 ayat (1) huruf b terdiri atas: a. tarif Pelayanan dasar; dan b. tarif Pelayanan nondasar.",
      },
      {
        label: "(2)",
        teks:
          "Tarif atas jenis PNBP yang berasal dari Pelayanan sebagaimana dimaksud pada ayat (1) disusun dengan mempertimbangkan:",
        sub: [
          { label: "a.", teks: "dampak pengenaan tarif terhadap masyarakat, dunia usaha, dan sosial budaya;" },
          { label: "b.", teks: "biaya penyelenggaraan layanan;" },
          { label: "c.", teks: "aspek keadilan; dan/atau" },
          { label: "d.", teks: "kebijakan Pemerintah." },
        ],
      },
      {
        label: "(3)",
        teks:
          "Tarif atas jenis PNBP yang berasal dari Pelayanan diatur dengan Peraturan Pemerintah dan/atau Peraturan Menteri.",
      },
    ],
    catatan:
      "Pelayanan dasar adalah Pelayanan Pemerintah dalam rangka pemenuhan kebutuhan dasar warga negara antara lain di bidang pendidikan, kesehatan, dan keamanan. Pelayanan nondasar antara lain di bidang perhubungan, perdagangan, perindustrian, dan pariwisata.",
  },

  "pasal-13": {
    key: "pasal-13",
    referensi: "Pasal 13 UU No. 9 Tahun 2018",
    bab: "BAB III — Tarif atas Jenis PNBP",
    judul: "Penetapan Tarif dengan Pertimbangan Tertentu",
    ayat: [
      {
        label: "",
        teks:
          "Dengan pertimbangan tertentu, tarif atas jenis PNBP dapat ditetapkan sampai dengan Rp0,00 (nol rupiah) atau 0% (nol persen).",
      },
    ],
    catatan:
      'Yang dimaksud dengan "pertimbangan tertentu" antara lain penyelenggaraan kegiatan sosial, kegiatan keagamaan, kegiatan kenegaraan, dan pertimbangan karena keadaan di luar kemampuan Wajib Bayar atau kondisi kahar, serta bagi masyarakat tidak mampu, mahasiswa berprestasi, dan usaha mikro, kecil, dan menengah.',
  },

  "pasal-15": {
    key: "pasal-15",
    referensi: "Pasal 15 UU No. 9 Tahun 2018",
    bab: "BAB IV — Kewenangan Pengelolaan PNBP",
    judul: "Kewenangan Menteri selaku Pengelola Fiskal",
    pembuka: "Menteri selaku pengelola fiskal dalam mengelola PNBP berwenang:",
    ayat: [
      { label: "a.", teks: "menyusun kebijakan umum Pengelolaan PNBP;" },
      {
        label: "b.",
        teks: "mengevaluasi, menyusun, dan/atau menetapkan jenis dan tarif PNBP pada Instansi Pengelola PNBP berdasarkan usulan dari Instansi Pengelola PNBP;",
      },
      {
        label: "c.",
        teks: "menetapkan target PNBP dan/atau pagu penggunaan dana PNBP dalam rangka penyusunan rancangan anggaran pendapatan dan belanja negara dan/atau rancangan anggaran pendapatan dan belanja negara perubahan;",
      },
      { label: "d.", teks: "menetapkan penggunaan dana PNBP;" },
      {
        label: "e.",
        teks: "melakukan pengawasan terhadap perencanaan, pelaksanaan, dan pertanggungjawaban PNBP;",
      },
      {
        label: "f.",
        teks: "meminta instansi pemeriksa untuk melakukan Pemeriksaan PNBP terhadap Instansi Pengelola PNBP, Wajib Bayar, dan/atau Mitra Instansi Pengelola PNBP;",
      },
      { label: "g.", teks: "menetapkan Pengelolaan PNBP lintas Instansi Pengelola PNBP; dan" },
      {
        label: "h.",
        teks: "melaksanakan kewenangan lain di bidang PNBP sesuai dengan ketentuan peraturan perundang-undangan.",
      },
    ],
    catatan: "Instansi pemeriksa yang dimaksud adalah BPKP (Badan Pengawasan Keuangan dan Pembangunan). BPK tetap dapat melaksanakan Pemeriksaan PNBP sesuai dengan ketentuan perundang-undangan.",
  },

  "pasal-16": {
    key: "pasal-16",
    referensi: "Pasal 16 UU No. 9 Tahun 2018",
    bab: "BAB IV — Kewenangan Pengelolaan PNBP",
    judul: "Instansi Pengelola PNBP",
    ayat: [
      {
        label: "(1)",
        teks: "Instansi Pengelola PNBP terdiri atas: a. Kementerian/Lembaga; dan b. Kementerian yang menjalankan fungsi sebagai Bendahara Umum Negara.",
      },
      {
        label: "(2)",
        teks: "Instansi Pengelola PNBP sebagaimana dimaksud pada ayat (1) huruf a dipimpin oleh Menteri/Pimpinan Lembaga selaku pengguna anggaran/pengguna barang.",
      },
      {
        label: "(3)",
        teks: "Instansi Pengelola PNBP sebagaimana dimaksud pada ayat (1) huruf b dipimpin oleh Menteri selaku Bendahara Umum Negara.",
      },
    ],
    catatan:
      "Menteri/Pimpinan Lembaga selaku pengguna anggaran/pengguna barang pada hakikatnya merupakan Chief Operational Officer (COO). Selain menjalankan fungsi COO, Menteri juga menjalankan fungsi Bendahara Umum Negara (Chief Financial Officer/CFO).",
  },

  "pasal-17": {
    key: "pasal-17",
    referensi: "Pasal 17 UU No. 9 Tahun 2018",
    bab: "BAB IV — Kewenangan Pengelolaan PNBP",
    judul: "Tugas Pimpinan Instansi Pengelola PNBP",
    pembuka: "Pimpinan Instansi Pengelola PNBP bertugas:",
    ayat: [
      { label: "a.", teks: "menyusun dan menyampaikan usulan jenis dan tarif PNBP;" },
      { label: "b.", teks: "mengusulkan penggunaan dana PNBP;" },
      {
        label: "c.",
        teks: "menyusun dan menyampaikan rencana PNBP dalam rangka penyusunan rancangan anggaran pendapatan dan belanja negara dan/atau rancangan anggaran pendapatan dan belanja negara perubahan;",
      },
      { label: "d.", teks: "memungut dan menyetorkan PNBP ke Kas Negara;" },
      {
        label: "e.",
        teks: "melaksanakan anggaran yang bersumber dari pagu penggunaan dana PNBP;",
      },
      { label: "f.", teks: "mengelola piutang PNBP;" },
      {
        label: "g.",
        teks: "menyusun dan menyampaikan laporan pertanggungjawaban PNBP;",
      },
      { label: "h.", teks: "menunjuk pejabat kuasa pengelola PNBP; dan" },
      {
        label: "i.",
        teks: "melaksanakan tugas lain di bidang PNBP pada Instansi Pengelola PNBP yang dipimpinnya sesuai dengan ketentuan peraturan perundang-undangan di bidang PNBP.",
      },
    ],
  },

  "pasal-19": {
    key: "pasal-19",
    referensi: "Pasal 19 UU No. 9 Tahun 2018",
    bab: "BAB IV — Kewenangan Pengelolaan PNBP",
    judul: "Mitra Instansi Pengelola PNBP",
    ayat: [
      {
        label: "(1)",
        teks:
          "Instansi Pengelola PNBP sebagaimana dimaksud dalam Pasal 16 ayat (1) dapat dibantu oleh Mitra Instansi Pengelola PNBP untuk melakukan pemungutan, penyetoran, dan/atau penagihan PNBP berdasarkan ketentuan peraturan perundang-undangan.",
      },
      {
        label: "(2)",
        teks:
          "Mitra Instansi Pengelola PNBP sebagaimana dimaksud pada ayat (1) wajib melakukan penatausahaan dan menyampaikan laporan PNBP kepada Instansi Pengelola PNBP.",
      },
      {
        label: "(3)",
        teks:
          "Ketentuan lebih lanjut mengenai Mitra Instansi Pengelola PNBP sebagaimana dimaksud pada ayat (1) diatur dengan Peraturan Pemerintah.",
      },
    ],
  },

  "pasal-22": {
    key: "pasal-22",
    referensi: "Pasal 22 UU No. 9 Tahun 2018",
    bab: "BAB V — Pengelolaan PNBP",
    judul: "Perencanaan PNBP",
    ayat: [
      {
        label: "(1)",
        teks:
          "Perencanaan sebagaimana dimaksud dalam Pasal 21 huruf a dilakukan untuk penyusunan rancangan anggaran pendapatan dan belanja negara dan/atau rancangan anggaran pendapatan dan belanja negara perubahan dengan mengikuti siklus anggaran pendapatan dan belanja negara.",
      },
      {
        label: "(2)",
        teks:
          "Perencanaan sebagaimana dimaksud pada ayat (1) disusun dalam bentuk rencana PNBP berupa: a. target PNBP; atau b. target dan pagu penggunaan dana PNBP.",
      },
      {
        label: "(3)",
        teks:
          "Rencana PNBP sebagaimana dimaksud pada ayat (2) disusun secara realistis, optimal, dan sesuai dengan ketentuan peraturan perundang-undangan.",
      },
    ],
    catatan:
      '"Realistis" berarti mempertimbangkan data historis, potensi, asumsi, dan informasi terkait yang dapat dipertanggungjawabkan. "Optimal" adalah jumlah PNBP yang paling baik yang bisa dicapai dalam suatu kondisi pada saat menyusun rencana PNBP.',
  },

  "pasal-23": {
    key: "pasal-23",
    referensi: "Pasal 23 UU No. 9 Tahun 2018",
    bab: "BAB V — Pengelolaan PNBP",
    judul: "Penetapan Target PNBP",
    ayat: [
      {
        label: "(1)",
        teks:
          "Rencana PNBP sebagaimana dimaksud dalam Pasal 22 wajib disampaikan oleh Instansi Pengelola PNBP kepada Menteri untuk tahun anggaran yang direncanakan.",
      },
      {
        label: "(2)",
        teks:
          "Rencana PNBP sebagaimana dimaksud pada ayat (1) ditetapkan oleh Menteri dengan mempertimbangkan masukan dari Instansi Pengelola PNBP.",
      },
      {
        label: "(3)",
        teks:
          "Dalam hal Instansi Pengelola PNBP tidak menyampaikan rencana PNBP sebagaimana dimaksud pada ayat (1), Menteri menetapkan rencana PNBP untuk Instansi Pengelola PNBP yang terkait.",
      },
      {
        label: "(4)",
        teks:
          "Rencana PNBP sebagaimana dimaksud pada ayat (2) dan ayat (3) dituangkan dalam rancangan anggaran pendapatan dan belanja negara dan/atau rancangan anggaran pendapatan dan belanja negara perubahan.",
      },
    ],
  },

  "pasal-26": {
    key: "pasal-26",
    referensi: "Pasal 26 UU No. 9 Tahun 2018",
    bab: "BAB V — Pengelolaan PNBP",
    judul: "Penghitungan PNBP Terutang",
    pembuka: "PNBP Terutang dihitung oleh:",
    ayat: [
      { label: "a.", teks: "Instansi Pengelola PNBP;" },
      { label: "b.", teks: "Mitra Instansi Pengelola PNBP; atau" },
      { label: "c.", teks: "Wajib Bayar." },
    ],
  },

  "pasal-28": {
    key: "pasal-28",
    referensi: "Pasal 28 UU No. 9 Tahun 2018",
    bab: "BAB V — Pengelolaan PNBP",
    judul: "Kewajiban Pemungutan PNBP",
    ayat: [
      {
        label: "(1)",
        teks:
          "Instansi Pengelola PNBP wajib melaksanakan pemungutan PNBP berdasarkan jenis dan tarif PNBP sesuai dengan ketentuan peraturan perundang-undangan.",
      },
      {
        label: "(2)",
        teks:
          "Instansi Pengelola PNBP yang tidak melaksanakan pemungutan PNBP berdasarkan ketentuan sebagaimana dimaksud pada ayat (1) dikenai sanksi sesuai dengan ketentuan peraturan perundang-undangan.",
      },
    ],
  },

  "pasal-29": {
    key: "pasal-29",
    referensi: "Pasal 29 UU No. 9 Tahun 2018",
    bab: "BAB V — Pengelolaan PNBP",
    judul: "Kewajiban Penyetoran ke Kas Negara",
    ayat: [
      {
        label: "",
        teks: "Seluruh PNBP wajib disetor ke Kas Negara.",
      },
    ],
  },

  "pasal-31": {
    key: "pasal-31",
    referensi: "Pasal 31 UU No. 9 Tahun 2018",
    bab: "BAB V — Pengelolaan PNBP",
    judul: "Jatuh Tempo dan Denda Keterlambatan",
    ayat: [
      {
        label: "(1)",
        teks:
          "Wajib Bayar wajib membayar PNBP Terutang sebagaimana dimaksud dalam Pasal 30 ayat (1) dan ayat (2) paling lambat pada saat jatuh tempo sesuai dengan ketentuan peraturan perundang-undangan.",
      },
      {
        label: "(2)",
        teks:
          "Wajib Bayar yang tidak melakukan pembayaran PNBP Terutang sampai dengan jatuh tempo sebagaimana dimaksud pada ayat (1) dikenai sanksi administratif.",
      },
      {
        label: "(3)",
        teks:
          "Sanksi administratif sebagaimana dimaksud pada ayat (2) berupa denda sebesar 2% (dua persen) per bulan dari jumlah PNBP Terutang dan bagian dari bulan dihitung satu bulan penuh.",
      },
      {
        label: "(4)",
        teks:
          "Sanksi administratif berupa denda sebagaimana dimaksud pada ayat (3) dikenakan untuk waktu paling lama 24 (dua puluh empat) bulan.",
      },
    ],
  },

  "pasal-33": {
    key: "pasal-33",
    referensi: "Pasal 33 UU No. 9 Tahun 2018",
    bab: "BAB V — Pengelolaan PNBP",
    judul: "Penggunaan Dana PNBP",
    ayat: [
      {
        label: "(1)",
        teks:
          "Instansi Pengelola PNBP sebagaimana dimaksud dalam Pasal 16 ayat (1) huruf a dapat mengusulkan penggunaan dana PNBP yang dikelolanya kepada Menteri.",
      },
      {
        label: "(2)",
        teks:
          "Terhadap usulan penggunaan dana PNBP sebagaimana dimaksud pada ayat (1), Menteri memberikan persetujuan atau penolakan dengan mempertimbangkan: a. kondisi keuangan negara; b. kebijakan fiskal; dan/atau c. kebutuhan pendanaan Instansi Pengelola PNBP.",
      },
      {
        label: "(3)",
        teks:
          "Penggunaan dana PNBP sebagaimana dimaksud pada ayat (1) dapat digunakan oleh Instansi Pengelola PNBP untuk unit-unit kerja di lingkungannya dalam rangka: a. penyelenggaraan Pengelolaan PNBP dan/atau peningkatan kualitas penyelenggaraan Pengelolaan PNBP dan/atau kegiatan lainnya; dan/atau b. optimalisasi PNBP.",
      },
    ],
  },

  "pasal-35": {
    key: "pasal-35",
    referensi: "Pasal 35 UU No. 9 Tahun 2018",
    bab: "BAB V — Pengelolaan PNBP",
    judul: "Pengelolaan Piutang PNBP",
    ayat: [
      {
        label: "(1)",
        teks:
          "Dalam hal Wajib Bayar belum melakukan pembayaran PNBP Terutang, Instansi Pengelola PNBP mencatat PNBP Terutang sebagai piutang PNBP.",
      },
      {
        label: "(2)",
        teks:
          "Instansi Pengelola PNBP wajib mengelola piutang PNBP yang menjadi tanggung jawabnya sesuai dengan ketentuan peraturan perundang-undangan di bidang piutang negara.",
      },
      {
        label: "(3)",
        teks:
          "Instansi Pengelola PNBP yang tidak melaksanakan pengelolaan piutang PNBP sebagaimana dimaksud pada ayat (2) dikenai sanksi sesuai dengan ketentuan peraturan perundang-undangan.",
      },
    ],
  },

  "pasal-36": {
    key: "pasal-36",
    referensi: "Pasal 36 UU No. 9 Tahun 2018",
    bab: "BAB V — Pengelolaan PNBP",
    judul: "Penetapan PNBP Terutang (Kurang Bayar)",
    ayat: [
      {
        label: "(1)",
        teks:
          "Dalam hal terjadi kurang bayar terhadap PNBP Terutang sebagaimana dimaksud dalam Pasal 31 ayat (1) dan ayat (2), Instansi Pengelola PNBP atau Mitra Instansi Pengelola PNBP menetapkan PNBP Terutang.",
      },
      {
        label: "(2)",
        teks: "Penetapan PNBP Terutang sebagaimana dimaksud pada ayat (1) didasarkan pada:",
        sub: [
          { label: "a.", teks: "hasil verifikasi dan/atau monitoring oleh Instansi Pengelola PNBP atau Mitra Instansi Pengelola PNBP;" },
          { label: "b.", teks: "laporan hasil pemeriksaan terhadap Wajib Bayar;" },
          { label: "c.", teks: "putusan pengadilan; dan/atau" },
          { label: "d.", teks: 'sumber lainnya (antara lain hasil temuan audit BPK).' },
        ],
      },
    ],
  },

  "pasal-37": {
    key: "pasal-37",
    referensi: "Pasal 37 UU No. 9 Tahun 2018",
    bab: "BAB V — Pengelolaan PNBP",
    judul: "Penerbitan Surat Tagihan PNBP",
    ayat: [
      {
        label: "(1)",
        teks:
          "Penetapan PNBP Terutang sebagaimana dimaksud dalam Pasal 36 ayat (2) huruf a, huruf c, dan huruf d, wajib dilakukan oleh Instansi Pengelola PNBP atau Mitra Instansi Pengelola PNBP dengan menerbitkan dan menyampaikan Surat Tagihan PNBP kepada Wajib Bayar.",
      },
      {
        label: "(2)",
        teks:
          "Penetapan PNBP Terutang sebagaimana dimaksud dalam Pasal 36 ayat (2) huruf b, wajib dilakukan oleh Instansi Pengelola PNBP atau Mitra Instansi Pengelola PNBP dengan menerbitkan dan menyampaikan Surat Ketetapan PNBP kurang bayar dan Surat Tagihan PNBP kepada Wajib Bayar.",
      },
      {
        label: "(3)",
        teks:
          "Dalam hal Instansi Pengelola PNBP atau Mitra Instansi Pengelola PNBP tidak memenuhi kewajiban sebagaimana dimaksud pada ayat (1) dan ayat (2), dikenai sanksi sesuai dengan ketentuan peraturan perundang-undangan.",
      },
    ],
  },

  "pasal-38": {
    key: "pasal-38",
    referensi: "Pasal 38 UU No. 9 Tahun 2018",
    bab: "BAB V — Pengelolaan PNBP",
    judul: "Koreksi Surat Tagihan PNBP",
    ayat: [
      {
        label: "(1)",
        teks:
          "Dalam hal Wajib Bayar tidak setuju atas Surat Tagihan PNBP sebagaimana dimaksud dalam Pasal 37 ayat (1), Wajib Bayar dapat mengajukan permohonan koreksi terhadap Surat Tagihan PNBP secara tertulis kepada Instansi Pengelola PNBP dan/atau Mitra Instansi Pengelola PNBP.",
      },
      {
        label: "(2)",
        teks:
          "Instansi Pengelola PNBP dan/atau Mitra Instansi Pengelola PNBP memberikan jawaban kepada Wajib Bayar atas permohonan koreksi terhadap Surat Tagihan PNBP sebagaimana dimaksud pada ayat (1).",
      },
    ],
    catatan:
      '"Wajib Bayar tidak setuju" antara lain disebabkan kesalahan tulis dan kesalahan hitung. Jawaban dapat berupa penetapan kembali jumlah PNBP Terutang yang sama atau jumlah PNBP Terutang baru, disertai penjelasan atas disetujui atau ditolaknya permohonan koreksi.',
  },

  "pasal-41": {
    key: "pasal-41",
    referensi: "Pasal 41 UU No. 9 Tahun 2018",
    bab: "BAB V — Pengelolaan PNBP",
    judul: "Penatausahaan PNBP",
    ayat: [
      {
        label: "(1)",
        teks:
          "Instansi Pengelola PNBP dan Wajib Bayar yang menghitung sendiri PNBP Terutang wajib menatausahakan PNBP.",
      },
      {
        label: "(2)",
        teks:
          "Penatausahaan PNBP sebagaimana dimaksud pada ayat (1), wajib diselenggarakan di wilayah yurisdiksi Indonesia dan disusun dalam: a. bahasa Indonesia dengan menggunakan satuan mata uang Rupiah; dan/atau b. bahasa asing dengan menggunakan satuan mata uang asing yang diizinkan oleh Menteri.",
      },
      {
        label: "(3)",
        teks:
          "Dokumen yang menjadi dasar penatausahaan PNBP sebagaimana dimaksud pada ayat (1) wajib disimpan selama 10 (sepuluh) tahun.",
      },
      {
        label: "(5)",
        teks:
          "Dalam hal Wajib Bayar tidak memenuhi kewajiban sebagaimana dimaksud pada ayat (1) dikenai sanksi administratif berupa denda sebesar Rp10.000.000,00 (sepuluh juta rupiah).",
      },
    ],
  },

  "pasal-42": {
    key: "pasal-42",
    referensi: "Pasal 42 UU No. 9 Tahun 2018",
    bab: "BAB V — Pengelolaan PNBP",
    judul: "Pelaporan oleh Wajib Bayar",
    ayat: [
      {
        label: "(1)",
        teks:
          "Dalam rangka pertanggungjawaban PNBP, Wajib Bayar yang menghitung sendiri PNBP Terutang wajib menyampaikan laporan realisasi PNBP dan laporan PNBP Terutang kepada Instansi Pengelola PNBP.",
      },
      {
        label: "(2)",
        teks:
          "Laporan realisasi PNBP dan laporan PNBP Terutang sebagaimana dimaksud pada ayat (1) paling sedikit memuat jenis, periode, dan jumlah PNBP.",
      },
      {
        label: "(3)",
        teks:
          "Laporan realisasi PNBP dan laporan PNBP Terutang sebagaimana dimaksud pada ayat (2) wajib disampaikan secara periodik paling lama 20 (dua puluh) hari kalender setelah periode laporan tersebut berakhir.",
      },
      {
        label: "(4)",
        teks:
          "Dalam hal Wajib Bayar tidak menyampaikan laporan realisasi PNBP dan laporan PNBP Terutang sampai dengan batas waktu sebagaimana dimaksud pada ayat (3), dikenai sanksi administratif berupa denda sebesar Rp1.000.000,00 (satu juta rupiah).",
      },
    ],
  },

  "pasal-43": {
    key: "pasal-43",
    referensi: "Pasal 43 UU No. 9 Tahun 2018",
    bab: "BAB V — Pengelolaan PNBP",
    judul: "Pelaporan oleh Instansi Pengelola PNBP",
    ayat: [
      {
        label: "(1)",
        teks:
          "Dalam rangka pertanggungjawaban pelaksanaan anggaran pendapatan dan belanja negara, Instansi Pengelola PNBP wajib menyampaikan laporan realisasi penerimaan dan penggunaan dana PNBP dalam lingkungan Instansi Pengelola PNBP yang bersangkutan kepada Menteri.",
      },
      {
        label: "(2)",
        teks:
          "Laporan realisasi penerimaan dan penggunaan dana PNBP sebagaimana dimaksud pada ayat (1) paling sedikit memuat jenis, periode, jumlah PNBP, dan jumlah penggunaan dana PNBP.",
      },
    ],
  },

  "pasal-45": {
    key: "pasal-45",
    referensi: "Pasal 45 UU No. 9 Tahun 2018",
    bab: "BAB V — Pengelolaan PNBP",
    judul: "Pengawasan Intern",
    ayat: [
      {
        label: "(1)",
        teks:
          "Setiap Instansi Pengelola PNBP sebagaimana dimaksud dalam Pasal 16 ayat (1) melaksanakan pengawasan intern atas Pengelolaan PNBP sesuai dengan ketentuan peraturan perundang-undangan.",
      },
      {
        label: "(2)",
        teks:
          "Pengawasan intern atas Pengelolaan PNBP sebagaimana dimaksud pada ayat (1) dilakukan oleh aparat pengawasan intern pemerintah yang bertanggung jawab langsung kepada Menteri/Pimpinan Lembaga.",
      },
    ],
  },

  "pasal-46": {
    key: "pasal-46",
    referensi: "Pasal 46 UU No. 9 Tahun 2018",
    bab: "BAB V — Pengelolaan PNBP",
    judul: "Pengawasan oleh Menteri",
    ayat: [
      {
        label: "(1)",
        teks:
          "Untuk meningkatkan kualitas perencanaan, pelaksanaan, dan pertanggungjawaban PNBP, Menteri melakukan pengawasan terhadap Instansi Pengelola PNBP.",
      },
      {
        label: "(2)",
        teks:
          "Pengawasan sebagaimana dimaksud pada ayat (1) dapat dilakukan dalam bentuk verifikasi, penilaian, dan/atau evaluasi.",
      },
      {
        label: "(3)",
        teks:
          "Untuk efektivitas pelaksanaan pengawasan sebagaimana dimaksud pada ayat (1), Menteri dapat melakukan penguatan organisasi yang melaksanakan fungsi dimaksud sesuai dengan ketentuan peraturan perundang-undangan.",
      },
    ],
  },

  "pasal-47": {
    key: "pasal-47",
    referensi: "Pasal 47 UU No. 9 Tahun 2018",
    bab: "BAB VI — Pemeriksaan PNBP",
    judul: "Dasar Pemeriksaan PNBP terhadap Wajib Bayar",
    ayat: [
      {
        label: "(1)",
        teks:
          "Terhadap Wajib Bayar yang menghitung sendiri kewajiban PNBP Terutang sebagaimana dimaksud dalam Pasal 26 huruf c, atas permintaan Pimpinan Instansi Pengelola PNBP, dapat dilakukan Pemeriksaan PNBP oleh instansi pemeriksa.",
      },
      {
        label: "(2)",
        teks: "Permintaan Pimpinan Instansi Pengelola PNBP sebagaimana dimaksud pada ayat (1), dilakukan berdasarkan:",
        sub: [
          { label: "a.", teks: "hasil pengawasan Instansi Pengelola PNBP terhadap Wajib Bayar yang bersangkutan;" },
          { label: "b.", teks: "permohonan pengembalian kelebihan pembayaran PNBP; dan/atau" },
          { label: "c.", teks: "permohonan keringanan PNBP Terutang." },
        ],
      },
    ],
    catatan:
      "Pemeriksaan PNBP bertujuan untuk menguji kepatuhan atas pemenuhan kewajiban orang pribadi atau Badan dan pemenuhan ketentuan peraturan perundang-undangan di bidang PNBP, bukan untuk menilai atau memberikan opini tentang laporan keuangan. Instansi pemeriksa = BPKP.",
  },

  "pasal-52": {
    key: "pasal-52",
    referensi: "Pasal 52 UU No. 9 Tahun 2018",
    bab: "BAB VI — Pemeriksaan PNBP",
    judul: "Ruang Lingkup Pemeriksaan PNBP",
    ayat: [
      {
        label: "(2)",
        teks:
          "Pemeriksaan PNBP terhadap Wajib Bayar yang menghitung sendiri kewajiban PNBP Terutang sebagaimana dimaksud dalam Pasal 26 huruf c termasuk pemeriksaan atas: a. laporan keuangan serta dokumen pendukung lain yang berkaitan dengan objek Pemeriksaan PNBP; dan b. bukti transaksi keuangan yang berkaitan dengan pembayaran dan/atau penyetoran PNBP.",
      },
      {
        label: "(3)",
        teks:
          "Pemeriksaan PNBP terhadap Instansi Pengelola PNBP termasuk pemeriksaan atas: a. sistem pengendalian intern terkait pengelolaan PNBP; dan b. bukti transaksi keuangan yang berkaitan dengan pembayaran dan/atau penyetoran PNBP.",
      },
    ],
  },

  "pasal-58": {
    key: "pasal-58",
    referensi: "Pasal 58 UU No. 9 Tahun 2018",
    bab: "BAB VII — Keberatan PNBP",
    judul: "Hak Mengajukan Keberatan",
    ayat: [
      {
        label: "(1)",
        teks: "Wajib Bayar dapat mengajukan keberatan kepada Instansi Pengelola PNBP atas:",
        sub: [
          { label: "a.", teks: "Surat Ketetapan PNBP Kurang Bayar;" },
          { label: "b.", teks: "Surat Ketetapan PNBP Nihil; atau" },
          { label: "c.", teks: "Surat Ketetapan PNBP Lebih Bayar." },
        ],
      },
      {
        label: "(2)",
        teks: "Keberatan diajukan secara tertulis dengan mengemukakan alasan pengajuan keberatan.",
      },
      {
        label: "(3)",
        teks:
          "Pengajuan keberatan terhadap Surat Ketetapan PNBP Kurang Bayar sebagaimana dimaksud pada ayat (1) huruf a tidak menunda kewajiban membayar PNBP Terutang.",
      },
    ],
  },

  "pasal-59": {
    key: "pasal-59",
    referensi: "Pasal 59 UU No. 9 Tahun 2018",
    bab: "BAB VII — Keberatan PNBP",
    judul: "Prosedur dan Jangka Waktu Keberatan",
    ayat: [
      {
        label: "(1)",
        teks:
          "Pengajuan keberatan sebagaimana dimaksud dalam Pasal 58 ayat (1) disertai dokumen pendukung yang lengkap dan diajukan dalam jangka waktu 3 (tiga) bulan sejak tanggal Surat Ketetapan PNBP.",
      },
      {
        label: "(3)",
        teks:
          "Paling lambat dalam jangka waktu 6 (enam) bulan setelah surat keberatan dan dokumen pendukung diterima secara lengkap, Pimpinan Instansi Pengelola PNBP atau pejabat kuasa pengelola PNBP, mengeluarkan penetapan atas pengajuan keberatan.",
      },
      {
        label: "(4)",
        teks:
          "Apabila Pimpinan Instansi Pengelola PNBP atau pejabat kuasa pengelola PNBP tidak mengeluarkan penetapan sesuai jangka waktu sebagaimana dimaksud pada ayat (3), pengajuan keberatan yang diajukan Wajib Bayar tersebut dianggap dikabulkan.",
      },
    ],
  },

  "pasal-60": {
    key: "pasal-60",
    referensi: "Pasal 60 UU No. 9 Tahun 2018",
    bab: "BAB VII — Keberatan PNBP",
    judul: "Sifat Final Penetapan Keberatan",
    ayat: [
      {
        label: "(1)",
        teks:
          "Penetapan oleh pimpinan Instansi Pengelola PNBP atau pejabat kuasa pengelola PNBP atas pengajuan keberatan sebagaimana dimaksud dalam Pasal 59 bersifat final.",
      },
      {
        label: "(2)",
        teks:
          "Dalam hal Wajib Bayar tidak setuju terhadap penetapan atas pengajuan keberatan sebagaimana dimaksud pada ayat (1), Wajib Bayar dapat mengajukan gugatan melalui Pengadilan Tinggi Tata Usaha Negara.",
      },
    ],
    catatan:
      '"Penetapan bersifat final" merupakan keputusan administratif yang terakhir dari Pejabat Tata Usaha Negara. Pengadilan Tinggi TUN berwenang memeriksa, memutus, dan menyelesaikan gugatan sesuai ketentuan perundang-undangan.',
  },

  "pasal-62": {
    key: "pasal-62",
    referensi: "Pasal 62 UU No. 9 Tahun 2018",
    bab: "BAB VIII — Keringanan PNBP",
    judul: "Keringanan PNBP Terutang",
    ayat: [
      {
        label: "(1)",
        teks:
          "Dalam hal tertentu, Wajib Bayar dapat mengajukan permohonan keringanan PNBP Terutang kepada Instansi Pengelola PNBP.",
      },
      {
        label: "(2)",
        teks: "Hal tertentu sebagaimana dimaksud pada ayat (1) meliputi:",
        sub: [
          { label: "a.", teks: "di luar kemampuan Wajib Bayar atau kondisi kahar;" },
          { label: "b.", teks: "kesulitan likuiditas; dan/atau" },
          { label: "c.", teks: "kebijakan Pemerintah." },
        ],
      },
      {
        label: "(4)",
        teks: "Surat persetujuan atas permohonan keringanan PNBP sebagaimana dimaksud pada ayat (3), meliputi:",
        sub: [
          { label: "a.", teks: "penundaan;" },
          { label: "b.", teks: "pengangsuran;" },
          { label: "c.", teks: "pengurangan; dan/atau" },
          { label: "d.", teks: "pembebasan." },
        ],
      },
    ],
    catatan:
      '"Kondisi kesulitan likuiditas" adalah kondisi keuangan Wajib Bayar yang tidak dapat memenuhi kewajiban jangka pendek. Keringanan berupa pengurangan dan pembebasan memerlukan persetujuan Menteri.',
  },

  "pasal-63": {
    key: "pasal-63",
    referensi: "Pasal 63 UU No. 9 Tahun 2018",
    bab: "BAB IX — Pengembalian PNBP",
    judul: "Permohonan Pengembalian Kelebihan Pembayaran",
    ayat: [
      {
        label: "(1)",
        teks: "Permohonan pengembalian atas kelebihan pembayaran PNBP dapat diajukan oleh Wajib Bayar dalam hal terdapat:",
        sub: [
          { label: "a.", teks: "kesalahan pembayaran PNBP;" },
          { label: "b.", teks: "kesalahan pemungutan PNBP oleh Instansi Pengelola PNBP dan/atau Mitra Instansi Pengelola PNBP;" },
          { label: "c.", teks: "penetapan pimpinan Instansi Pengelola PNBP atau pejabat kuasa pengelola PNBP atas pengajuan keberatan PNBP;" },
          { label: "d.", teks: "putusan pengadilan yang telah mempunyai kekuatan hukum yang tetap;" },
          { label: "e.", teks: "hasil pemeriksaan instansi pemeriksa;" },
          { label: "f.", teks: "pelayanan yang tidak dapat dipenuhi oleh Instansi Pengelola PNBP dan/atau Mitra Instansi Pengelola PNBP secara sepihak; dan/atau" },
          { label: "g.", teks: "ketentuan peraturan perundang-undangan." },
        ],
      },
      {
        label: "(4)",
        teks:
          "Batas waktu permohonan pengembalian atas kelebihan pembayaran PNBP sebagaimana dimaksud pada ayat (1) huruf a, huruf b, huruf c, huruf f, dan huruf g, tidak melebihi jangka waktu 5 (lima) tahun sejak terjadinya kelebihan pembayaran PNBP.",
      },
      {
        label: "(5)",
        teks:
          "Batas waktu permohonan pengembalian atas kelebihan pembayaran PNBP sebagaimana dimaksud pada ayat (1) huruf d dan huruf e tidak melebihi jangka waktu 2 (dua) tahun sejak ditetapkannya putusan pengadilan atau diterbitkannya laporan hasil pemeriksaan.",
      },
    ],
  },

  "pasal-67": {
    key: "pasal-67",
    referensi: "Pasal 67 UU No. 9 Tahun 2018",
    bab: "BAB XI — Ketentuan Pidana",
    judul: "Pidana bagi Wajib Bayar yang Tidak Membayar",
    ayat: [
      {
        label: "",
        teks:
          "Wajib Bayar yang menghitung sendiri kewajiban PNBP sebagaimana dimaksud dalam Pasal 26 huruf c yang dengan sengaja tidak membayar atau menyampaikan laporan PNBP Terutang yang tidak benar, dipidana dengan pidana denda sebanyak 4 (empat) kali jumlah PNBP Terutang dan pidana penjara paling singkat 2 (dua) tahun dan paling lama 6 (enam) tahun.",
      },
    ],
  },

  "pasal-68": {
    key: "pasal-68",
    referensi: "Pasal 68 UU No. 9 Tahun 2018",
    bab: "BAB XI — Ketentuan Pidana",
    judul: "Pidana bagi yang Tidak Memberikan Dokumen/Keterangan",
    ayat: [
      {
        label: "",
        teks:
          "Setiap orang yang dengan sengaja tidak memberikan dokumen, keterangan, dan/atau bukti lain yang dimiliki sebagaimana dimaksud dalam Pasal 54 ayat (2), atau memberikan dokumen, keterangan, dan/atau bukti lain yang dimiliki namun isinya tidak benar, dipidana dengan pidana denda paling banyak Rp1.000.000.000,00 (satu miliar rupiah) atau pidana kurungan paling lama 1 (satu) tahun.",
      },
    ],
  },

  // ─── PP No. 44 Tahun 2025 ─────────────────────────────────────────────────
  // Tata Cara Penetapan Tarif, Pengelolaan, dan Penyelesaian Keberatan,
  // Keringanan, dan Pengembalian PNBP. Menggantikan PP 58/2020, PP 59/2020,
  // dan PP 69/2020.

  "pp44-pasal-1": {
    key: "pp44-pasal-1",
    referensi: "Pasal 1 PP No. 44 Tahun 2025",
    bab: "BAB I — Ketentuan Umum",
    judul: "Definisi-Definisi Pokok dalam PP 44/2025",
    pembuka: "Dalam Peraturan Pemerintah ini yang dimaksud dengan:",
    ayat: [
      { label: "1.", teks: "PNBP adalah pungutan yang dibayar oleh orang pribadi atau badan dengan memperoleh manfaat langsung maupun tidak langsung atas layanan atau pemanfaatan sumber daya dan hak yang diperoleh negara, berdasarkan peraturan perundang-undangan, yang menjadi penerimaan Pemerintah Pusat di luar penerimaan perpajakan dan hibah dan dikelola dalam mekanisme APBN." },
      { label: "5.", teks: "Pengelolaan PNBP adalah pemanfaatan sumber daya dalam rangka tata kelola yang meliputi kegiatan perencanaan, pelaksanaan, pertanggungjawaban, dan pengawasan untuk meningkatkan pelayanan, akuntabilitas, dan optimalisasi penerimaan negara yang berasal dari PNBP." },
      { label: "6.", teks: "Rencana PNBP adalah hasil penghitungan dan/atau penetapan target PNBP atau target dan pagu penggunaan dana PNBP yang diperkirakan dalam satu tahun anggaran." },
      { label: "8.", teks: "PNBP Terutang adalah kewajiban PNBP dari Wajib Bayar kepada Pemerintah yang wajib dibayar pada waktu tertentu sesuai dengan ketentuan peraturan perundang-undangan." },
      { label: "9.", teks: "Surat Tagihan PNBP adalah surat dan/atau dokumen yang digunakan untuk melakukan tagihan PNBP Terutang, baik berupa pokok maupun sanksi administratif berupa denda." },
      { label: "11.", teks: "Wajib Bayar adalah orang pribadi atau Badan dari dalam negeri atau luar negeri yang mempunyai kewajiban membayar PNBP sesuai dengan ketentuan peraturan perundang-undangan." },
      { label: "13.", teks: "Instansi Pengelola PNBP adalah instansi yang menyelenggarakan Pengelolaan PNBP." },
      { label: "14.", teks: "Mitra Instansi Pengelola PNBP adalah Badan yang membantu Instansi Pengelola PNBP melaksanakan sebagian kegiatan Pengelolaan PNBP yang menjadi tugas Instansi Pengelola PNBP berdasarkan ketentuan peraturan perundang-undangan." },
    ],
  },

  "pp44-pasal-2": {
    key: "pp44-pasal-2",
    referensi: "Pasal 2 PP No. 44 Tahun 2025",
    bab: "BAB II — Pengelola PNBP",
    judul: "Pengelola PNBP",
    pembuka: "Pengelola PNBP terdiri atas:",
    ayat: [
      { label: "a.", teks: "Menteri selaku pengelola fiskal; dan" },
      { label: "b.", teks: "Pimpinan Instansi Pengelola PNBP." },
    ],
  },

  "pp44-pasal-4": {
    key: "pp44-pasal-4",
    referensi: "Pasal 4 PP No. 44 Tahun 2025",
    bab: "BAB II — Pengelola PNBP",
    judul: "Instansi Pengelola PNBP",
    ayat: [
      {
        label: "(1)",
        teks: "Instansi Pengelola PNBP terdiri atas:",
        sub: [
          { label: "a.", teks: "Kementerian/Lembaga; dan" },
          { label: "b.", teks: "Kementerian yang menjalankan fungsi sebagai Bendahara Umum Negara." },
        ],
      },
      { label: "(2)", teks: "Instansi Pengelola PNBP sebagaimana dimaksud pada ayat (1) huruf a dipimpin oleh Menteri/Pimpinan Lembaga selaku pengguna anggaran/pengguna barang." },
      { label: "(3)", teks: "Instansi Pengelola PNBP sebagaimana dimaksud pada ayat (1) huruf b dipimpin oleh Menteri selaku Bendahara Umum Negara." },
      {
        label: "(4)",
        teks: "Dalam melaksanakan Pengelolaan PNBP, Pimpinan Instansi Pengelola PNBP sebagaimana dimaksud pada ayat (2) dan ayat (3) dapat:",
        sub: [
          { label: "a.", teks: "menunjuk Pejabat Kuasa Pengelola PNBP untuk melaksanakan sebagian tugas Pengelolaan PNBP; dan/atau" },
          { label: "b.", teks: "dibantu oleh Mitra Instansi Pengelola PNBP untuk melaksanakan sebagian tugas Pengelolaan PNBP." },
        ],
      },
    ],
  },

  "pp44-pasal-5": {
    key: "pp44-pasal-5",
    referensi: "Pasal 5 PP No. 44 Tahun 2025",
    bab: "BAB II — Pengelola PNBP",
    judul: "Penunjukan Mitra Instansi Pengelola PNBP",
    ayat: [
      {
        label: "(1)",
        teks: "Mitra Instansi Pengelola PNBP dapat ditunjuk berdasarkan:",
        sub: [
          { label: "a.", teks: "ketentuan peraturan perundang-undangan; atau" },
          { label: "b.", teks: "penugasan dari Instansi Pengelola PNBP dalam melaksanakan Pengelolaan PNBP dengan tetap memperhatikan tanggung jawab Instansi Pengelola PNBP." },
        ],
      },
      { label: "(2)", teks: "Penugasan dari Instansi Pengelola PNBP kepada Mitra Instansi Pengelola PNBP ditetapkan dalam kontrak/perjanjian." },
      { label: "(3)", teks: "Penugasan dilaksanakan secara efisien, efektif, terbuka dan bersaing, transparan, adil atau tidak diskriminatif, dan akuntabel." },
      {
        label: "(4)",
        teks: "Penugasan dilaksanakan dengan mempertimbangkan paling sedikit:",
        sub: [
          { label: "a.", teks: "mendukung tugas dan fungsi Instansi Pengelola PNBP;" },
          { label: "b.", teks: "dampak terhadap APBN dan/atau masyarakat;" },
          { label: "c.", teks: "peningkatan kualitas layanan; dan" },
          { label: "d.", teks: "optimalisasi PNBP." },
        ],
      },
      { label: "(5)", teks: "Penugasan dari Instansi Pengelola PNBP kepada Mitra Instansi Pengelola PNBP harus terlebih dahulu mendapatkan persetujuan Menteri." },
    ],
  },

  "pp44-pasal-6": {
    key: "pp44-pasal-6",
    referensi: "Pasal 6 PP No. 44 Tahun 2025",
    bab: "BAB II — Pengelola PNBP",
    judul: "Tugas Mitra Instansi Pengelola PNBP",
    ayat: [
      { label: "(1)", teks: "Mitra Instansi Pengelola PNBP dapat diberikan tugas membantu Instansi Pengelola PNBP untuk melakukan pemungutan, penyetoran, dan/atau penagihan PNBP sesuai dengan ketentuan peraturan perundang-undangan." },
      {
        label: "(2)",
        teks: "Dalam melaksanakan tugas sebagaimana dimaksud pada ayat (1), Mitra Instansi Pengelola PNBP juga dapat diberikan tugas yang meliputi:",
        sub: [
          { label: "a.", teks: "penentuan PNBP Terutang;" },
          { label: "b.", teks: "monitoring atau verifikasi atas PNBP Terutang;" },
          { label: "c.", teks: "pencatatan piutang PNBP; dan/atau" },
          { label: "d.", teks: "penyelesaian koreksi atas Surat Tagihan PNBP." },
        ],
      },
      { label: "(3)", teks: "Selain tugas sebagaimana dimaksud pada ayat (1) dan ayat (2), Mitra Instansi Pengelola PNBP dapat juga diberikan tugas dalam rangka peningkatan kualitas layanan kepada Wajib Bayar." },
    ],
  },

  "pp44-pasal-7": {
    key: "pp44-pasal-7",
    referensi: "Pasal 7 PP No. 44 Tahun 2025",
    bab: "BAB II — Pengelola PNBP",
    judul: "Imbal Jasa Mitra Instansi Pengelola PNBP",
    ayat: [
      { label: "(1)", teks: "Dalam hal Mitra Instansi Pengelola PNBP telah melaksanakan tugas sebagaimana dimaksud dalam Pasal 6, Mitra Instansi Pengelola PNBP dapat diberikan imbal jasa." },
      {
        label: "(2)",
        teks: "Imbal jasa dapat berupa:",
        sub: [
          { label: "a.", teks: "pemberian hak mendapatkan pendapatan atas layanan; dan/atau" },
          { label: "b.", teks: "dukungan pendanaan yang bersumber dari APBN." },
        ],
      },
      {
        label: "(3)",
        teks: "Pendapatan atas layanan dapat berupa:",
        sub: [
          { label: "a.", teks: "pembagian pendapatan dengan Instansi Pengelola PNBP sebagaimana diatur dalam peraturan perundang-undangan mengenai jenis dan tarif atas jenis PNBP;" },
          { label: "b.", teks: "pembagian pendapatan dengan Instansi Pengelola PNBP sebagaimana diatur dalam kontrak/perjanjian; dan/atau" },
          { label: "c.", teks: "pendapatan atas pelaksanaan tugas layanan lainnya sebagaimana dimaksud dalam Pasal 6 ayat (3)." },
        ],
      },
    ],
  },

  "pp44-pasal-8": {
    key: "pp44-pasal-8",
    referensi: "Pasal 8 PP No. 44 Tahun 2025",
    bab: "BAB II — Pengelola PNBP",
    judul: "Sanksi Administratif terhadap Mitra Instansi Pengelola PNBP",
    ayat: [
      { label: "(1)", teks: "Instansi Pengelola PNBP memberikan sanksi administratif kepada Mitra Instansi Pengelola PNBP yang tidak melaksanakan tugas sebagaimana dimaksud dalam Pasal 6." },
      {
        label: "(2)",
        teks: "Sanksi administratif terdiri atas:",
        sub: [
          { label: "a.", teks: "teguran tertulis;" },
          { label: "b.", teks: "denda administratif;" },
          { label: "c.", teks: "pemotongan imbal jasa;" },
          { label: "d.", teks: "penghapusan imbal jasa; dan" },
          { label: "e.", teks: "pencabutan status sebagai Mitra Instansi Pengelola PNBP." },
        ],
      },
      { label: "(3)", teks: "Sanksi administratif sebagaimana dimaksud pada ayat (2) dikenakan secara berjenjang." },
    ],
  },

  "pp44-pasal-10": {
    key: "pp44-pasal-10",
    referensi: "Pasal 10 PP No. 44 Tahun 2025",
    bab: "BAB III — Tata Cara Penetapan Tarif",
    judul: "Objek Penerimaan Negara Bukan Pajak",
    ayat: [
      {
        label: "(1)",
        teks: "Objek PNBP meliputi:",
        sub: [
          { label: "a.", teks: "pemanfaatan sumber daya alam;" },
          { label: "b.", teks: "pelayanan;" },
          { label: "c.", teks: "pengelolaan kekayaan negara dipisahkan;" },
          { label: "d.", teks: "pengelolaan barang milik negara;" },
          { label: "e.", teks: "pengelolaan dana; dan" },
          { label: "f.", teks: "hak negara lainnya." },
        ],
      },
      { label: "(2)", teks: "Objek PNBP sebagaimana dimaksud pada ayat (1) dirinci menurut jenis PNBP." },
      {
        label: "(3)",
        teks: "Jenis PNBP sebagaimana dimaksud pada ayat (2) diatur dengan:",
        sub: [
          { label: "a.", teks: "Undang-Undang;" },
          { label: "b.", teks: "Peraturan Pemerintah; dan/atau" },
          { label: "c.", teks: "Peraturan Menteri." },
        ],
      },
    ],
  },

  "pp44-pasal-11": {
    key: "pp44-pasal-11",
    referensi: "Pasal 11 PP No. 44 Tahun 2025",
    bab: "BAB III — Tata Cara Penetapan Tarif",
    judul: "Bentuk Tarif atas Jenis PNBP",
    pembuka: "Tarif atas jenis PNBP berbentuk:",
    ayat: [
      { label: "a.", teks: "tarif spesifik — ditetapkan dengan nilai nominal uang (contoh: Rp5.000.000,00/satuan); dan/atau" },
      { label: "b.", teks: "tarif ad valorem — ditetapkan dengan persentase atau formula (contoh: 10% × dasar perhitungan tertentu seperti harga patokan, harga jual, atau indeks harga)." },
    ],
  },

  "pp44-pasal-17": {
    key: "pp44-pasal-17",
    referensi: "Pasal 17 PP No. 44 Tahun 2025",
    bab: "BAB III — Tata Cara Penetapan Tarif",
    judul: "Tarif Berdasarkan Kontrak atau Bentuk Lain",
    ayat: [
      { label: "(1)", teks: "Tarif atas jenis PNBP yang berasal dari pelayanan, pengelolaan barang milik negara berupa penggunaan barang milik negara, pengelolaan dana, dan hak negara lainnya dapat dilaksanakan berdasarkan kontrak atau bentuk lain yang dipersamakan dengan kontrak." },
      {
        label: "(2)",
        teks: "Tarif berdasarkan kontrak harus memenuhi kriteria:",
        sub: [
          { label: "a.", teks: "tarif tidak dapat ditentukan di awal karena karakteristik pelaksanaan kegiatan Pemerintah;" },
          { label: "b.", teks: "komponen tarif ditentukan berdasarkan kesepakatan dengan Wajib Bayar; atau" },
          { label: "c.", teks: "jenis PNBP yang tarifnya ditentukan berdasarkan nilai guna barang dan/atau jasa pada saat terjadinya transaksi." },
        ],
      },
    ],
  },

  "pp44-pasal-18": {
    key: "pp44-pasal-18",
    referensi: "Pasal 18 PP No. 44 Tahun 2025",
    bab: "BAB III — Tata Cara Penetapan Tarif",
    judul: "Tarif dalam Peraturan Menteri/Lembaga",
    ayat: [
      {
        label: "(1)",
        teks: "Selain ketentuan sebagaimana dimaksud dalam Pasal 12 dan Pasal 14, tarif atas jenis PNBP dapat diatur dengan Peraturan Menteri/Lembaga sepanjang diperintahkan oleh:",
        sub: [
          { label: "a.", teks: "Undang-Undang; dan/atau" },
          { label: "b.", teks: "Peraturan Pemerintah." },
        ],
      },
      { label: "(2)", teks: "Besaran, persyaratan, dan tata cara pengaturan tarif atas jenis PNBP sebagaimana dimaksud pada ayat (1) harus terlebih dahulu mendapat persetujuan Menteri selaku pengelola fiskal." },
    ],
    catatan: "Persetujuan Menteri (Kemenkeu) merupakan syarat wajib sebelum setiap Kementerian/Lembaga menetapkan tarif melalui Peraturan Menteri.",
  },

  "pp44-pasal-19": {
    key: "pp44-pasal-19",
    referensi: "Pasal 19 PP No. 44 Tahun 2025",
    bab: "BAB III — Tata Cara Penetapan Tarif",
    judul: "Penyusunan dan Penyampaian Usulan Jenis dan Tarif PNBP",
    ayat: [
      { label: "(1)", teks: "Pimpinan Instansi Pengelola PNBP menyusun usulan jenis dan tarif atas jenis PNBP." },
      {
        label: "(2)",
        teks: "Dalam menyusun usulan jenis dan tarif atas jenis PNBP, Pimpinan Instansi Pengelola PNBP harus melakukan:",
        sub: [
          { label: "a.", teks: "upaya penyederhanaan jenis dan/atau tarif atas jenis PNBP;" },
          { label: "b.", teks: "analisis terhadap efektivitas dan kinerja pengenaan jenis dan tarif atas jenis PNBP;" },
          { label: "c.", teks: "analisis latar belakang pengenaan jenis dan tarif atas jenis PNBP beserta dasar kewenangan Instansi Pengelola PNBP;" },
          { label: "d.", teks: "analisis dasar perhitungan usulan jenis dan tarif atas jenis PNBP; dan/atau" },
          { label: "e.", teks: "analisis dampak pengenaan jenis dan tarif atas jenis PNBP." },
        ],
      },
      { label: "(3)", teks: "Pimpinan Instansi Pengelola PNBP menyampaikan usulan jenis dan tarif atas jenis PNBP disertai dengan hasil upaya dan/atau analisis yang telah dilakukan kepada Menteri selaku pengelola fiskal." },
    ],
  },

  "pp44-pasal-20": {
    key: "pp44-pasal-20",
    referensi: "Pasal 20 PP No. 44 Tahun 2025",
    bab: "BAB III — Tata Cara Penetapan Tarif",
    judul: "Evaluasi Usulan Jenis dan Tarif PNBP",
    ayat: [
      { label: "(1)", teks: "Menteri melakukan evaluasi atas usulan jenis dan tarif atas jenis PNBP sebagaimana dimaksud dalam Pasal 19 ayat (3)." },
      {
        label: "(2)",
        teks: "Evaluasi atas usulan jenis dan tarif atas jenis PNBP meliputi:",
        sub: [
          { label: "a.", teks: "evaluasi atas penerapan dasar pertimbangan dalam penyusunan tarif atas jenis PNBP berdasarkan ketentuan Undang-Undang mengenai PNBP; dan" },
          { label: "b.", teks: "evaluasi atas ketentuan yang harus dilakukan sebagaimana dimaksud dalam Pasal 19 ayat (2)." },
        ],
      },
      { label: "(3)", teks: "Hasil evaluasi dapat berupa penyesuaian dan/atau penyederhanaan atas usulan jenis dan tarif atas jenis PNBP, termasuk pengaturan tarif atas jenis PNBP sampai dengan Rp0,00 (nol rupiah) atau 0% (nol persen)." },
    ],
  },

  "pp44-pasal-25": {
    key: "pp44-pasal-25",
    referensi: "Pasal 25 PP No. 44 Tahun 2025",
    bab: "BAB III — Tata Cara Penetapan Tarif",
    judul: "Penetapan Tarif sampai dengan Rp0,00 atau 0%",
    ayat: [
      { label: "(1)", teks: "Dengan pertimbangan tertentu, tarif atas jenis PNBP dapat ditetapkan sampai dengan Rp0,00 (nol rupiah) atau 0% (nol persen)." },
      { label: "(2)", teks: "Ketentuan mengenai besaran, persyaratan, dan tata cara pengenaan tarif diatur dalam Peraturan Menteri/Lembaga." },
      { label: "(3)", teks: "Besaran, persyaratan, dan tata cara pengenaan tarif harus terlebih dahulu mendapat persetujuan Menteri selaku pengelola fiskal." },
    ],
    catatan: "Pertimbangan tertentu antara lain: penyelenggaraan kegiatan sosial, keagamaan, kenegaraan/pemerintahan (termasuk penyidikan dan perpajakan), keadaan kahar, masyarakat tidak mampu, mahasiswa berprestasi, UMKM, kebijakan pertumbuhan ekonomi, dan insentif fiskal.",
  },

  "pp44-pasal-31": {
    key: "pp44-pasal-31",
    referensi: "Pasal 31 PP No. 44 Tahun 2025",
    bab: "BAB IV — Pengelolaan PNBP",
    judul: "Pembayaran dan Penyetoran PNBP",
    ayat: [
      { label: "(1)", teks: "Wajib Bayar wajib membayar PNBP Terutang paling lambat pada saat jatuh tempo sesuai dengan ketentuan peraturan perundang-undangan." },
      { label: "(2)", teks: "Selain jatuh tempo sebagaimana dimaksud pada ayat (1), jatuh tempo ditetapkan oleh Pimpinan Instansi Pengelola PNBP atau Pejabat Kuasa Pengelola PNBP." },
      { label: "(3)", teks: "Dalam hal tertentu, Wajib Bayar dapat melakukan pembayaran PNBP Terutang melalui Instansi Pengelola PNBP atau Mitra Instansi Pengelola PNBP." },
      { label: "(4)", teks: "Instansi Pengelola PNBP atau Mitra Instansi Pengelola PNBP yang menerima pembayaran PNBP dari Wajib Bayar wajib menyetorkan seluruh PNBP pada waktunya ke Kas Negara sesuai dengan ketentuan peraturan perundang-undangan." },
      { label: "(5)", teks: "Wajib Bayar yang tidak melakukan pembayaran PNBP Terutang sampai dengan jatuh tempo dikenai sanksi administratif." },
      { label: "(6)", teks: "Sanksi administratif berupa denda sebesar 2% (dua persen) per bulan dari jumlah PNBP Terutang dan bagian dari bulan dihitung 1 (satu) bulan penuh." },
      { label: "(7)", teks: "Sanksi administratif berupa denda sebagaimana dimaksud pada ayat (6) dikenakan untuk waktu paling lama 24 (dua puluh empat) bulan." },
    ],
  },

  "pp44-pasal-46": {
    key: "pp44-pasal-46",
    referensi: "Pasal 46 PP No. 44 Tahun 2025",
    bab: "BAB IV — Pengelolaan PNBP",
    judul: "Penggunaan Dana PNBP",
    ayat: [
      { label: "(1)", teks: "Pimpinan Instansi Pengelola PNBP dapat mengusulkan penggunaan dana PNBP yang dikelolanya kepada Menteri." },
      {
        label: "(2)",
        teks: "Terhadap usulan penggunaan dana, Menteri memberikan persetujuan atau penolakan dengan mempertimbangkan:",
        sub: [
          { label: "a.", teks: "kondisi keuangan negara;" },
          { label: "b.", teks: "kebijakan fiskal; dan/atau" },
          { label: "c.", teks: "kebutuhan pendanaan Instansi Pengelola PNBP." },
        ],
      },
      {
        label: "(3)",
        teks: "Penggunaan dana PNBP dapat digunakan oleh Instansi Pengelola PNBP untuk mendukung:",
        sub: [
          { label: "a.", teks: "penyelenggaraan Pengelolaan PNBP dan peningkatan kualitas penyelenggaraan Pengelolaan PNBP; dan/atau" },
          { label: "b.", teks: "kegiatan lainnya." },
        ],
      },
    ],
  },

  "pp44-pasal-57": {
    key: "pp44-pasal-57",
    referensi: "Pasal 57 PP No. 44 Tahun 2025",
    bab: "BAB IV — Pengelolaan PNBP",
    judul: "Ruang Lingkup Pengawasan PNBP",
    pembuka: "Pengawasan PNBP dilakukan terhadap:",
    ayat: [
      { label: "a.", teks: "pemenuhan kewajiban PNBP; dan/atau" },
      { label: "b.", teks: "kepatuhan terhadap ketentuan peraturan perundang-undangan di bidang PNBP." },
    ],
  },
};
